/**
 * ApiKeysPanel — super-admin UI for per-tenant AI provider API keys.
 *
 * One section per provider (Anthropic / OpenAI / Google):
 *  - identity header: logo chip + provider name, with the EFFECTIVE key
 *    status line under it (own / inherited from ancestor / platform
 *    default — always masked to the last 4 characters);
 *  - the tenant's own keys as list rows: Primary/Backup rank badge,
 *    name + mono hint, scope select (subtree / tenant-only), health
 *    status chip, and an actions cluster (reorder / test / disable /
 *    delete);
 *  - an Add-key modal (plaintext is sent once over POST and encrypted
 *    at rest — it is never returned by any endpoint).
 *
 * Data source: GET/POST/PATCH/DELETE /super-admin/tenants/<id>/api-keys.
 * Styling: ApiKeysPanel.css (semantic design tokens, RTL-safe).
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Button, Card, Form, Input, Modal, Popconfirm, Radio, Select, Spin,
  Tooltip, message as antdMessage,
} from 'antd';
import {
  ArrowDownOutlined, ArrowUpOutlined, CheckCircleFilled, DeleteOutlined,
  ExperimentOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined,
  WarningFilled,
} from '@ant-design/icons';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { ProviderIcon } from '@/components/ProviderIcon';
import './ApiKeysPanel.css';

export interface TenantApiKey {
  key_id: string;
  provider: string;
  key_name: string;
  key_hint: string;
  priority: number;
  key_scope: 'subtree' | 'tenant_only';
  key_status: 'active' | 'disabled' | 'unhealthy';
  unhealthy_reason: string | null;
  unhealthy_at: string | null;
  last_used_at: string | null;
}

interface EffectiveKey {
  source: 'own' | 'inherited' | 'platform';
  source_tenant_id: string | null;
  source_tenant_name: string | null;
  key_name: string | null;
  key_hint: string | null;
  key_status: string;
}

interface ProviderKeys {
  effective: EffectiveKey;
  keys: TenantApiKey[];
}

interface ApiKeysPanelProps {
  tenantId: string;
}

const PROVIDERS: Array<{ id: string; label: string }> = [
  { id: 'anthropic', label: 'Anthropic' },
  { id: 'openai', label: 'OpenAI' },
  { id: 'google', label: 'Google' },
];

export function ApiKeysPanel({ tenantId }: ApiKeysPanelProps) {
  const { t } = useLanguage();
  const [providers, setProviders] = useState<Record<string, ProviderKeys>>({});
  const [loading, setLoading] = useState(true);
  const [busyKeyId, setBusyKeyId] = useState<string | null>(null);
  const [reorderingProvider, setReorderingProvider] = useState<string | null>(null);
  const [addModalProvider, setAddModalProvider] = useState<string | null>(null);
  const [addForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/super-admin/tenants/${tenantId}/api-keys`);
      setProviders(res.data?.providers || {});
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || String(err?.message || err));
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  async function patchKey(keyId: string, body: Record<string, unknown>) {
    setBusyKeyId(keyId);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/api-keys/${keyId}`, body);
      await load();
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('superAdmin.apiKeys.saveFailed'));
    } finally {
      setBusyKeyId(null);
    }
  }

  async function deleteKey(keyId: string) {
    setBusyKeyId(keyId);
    try {
      await api.delete(`/super-admin/tenants/${tenantId}/api-keys/${keyId}`);
      antdMessage.success(t('superAdmin.apiKeys.deleted'));
      await load();
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('superAdmin.apiKeys.saveFailed'));
    } finally {
      setBusyKeyId(null);
    }
  }

  async function testKey(keyId: string) {
    setBusyKeyId(keyId);
    try {
      const res = await api.post(`/super-admin/tenants/${tenantId}/api-keys/${keyId}/test`);
      if (res.data?.ok) {
        antdMessage.success(t('superAdmin.apiKeys.testOk'));
      } else {
        antdMessage.error(`${t('superAdmin.apiKeys.testFailed')}: ${res.data?.error || ''}`);
      }
      await load();
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('superAdmin.apiKeys.testFailed'));
    } finally {
      setBusyKeyId(null);
    }
  }

  // Reorder a provider's keys by moving one row up or down. The backend
  // renumbers to contiguous 0..N-1, so priority values never go negative.
  async function moveKey(provider: string, from: number, to: number) {
    const keys = [...(providers[provider]?.keys || [])];
    if (to < 0 || to >= keys.length) return;
    const [moved] = keys.splice(from, 1);
    keys.splice(to, 0, moved);
    const orderedKeyIds = keys.map(k => k.key_id);
    // Optimistic: show the new order immediately.
    setProviders(prev => ({ ...prev, [provider]: { ...prev[provider], keys } }));
    setReorderingProvider(provider);
    try {
      await api.patch(`/super-admin/tenants/${tenantId}/api-keys/reorder`, {
        provider,
        ordered_key_ids: orderedKeyIds,
      });
      await load();
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || t('superAdmin.apiKeys.saveFailed'));
      await load(); // revert to server truth
    } finally {
      setReorderingProvider(null);
    }
  }

  async function submitAddKey() {
    try {
      const values = await addForm.validateFields();
      setSaving(true);
      await api.post(`/super-admin/tenants/${tenantId}/api-keys`, {
        provider: addModalProvider,
        key_name: values.key_name,
        api_key: values.api_key,
        key_scope: values.key_scope,
      });
      antdMessage.success(t('superAdmin.apiKeys.created'));
      setAddModalProvider(null);
      addForm.resetFields();
      await load();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation error — already shown inline
      antdMessage.error(err?.response?.data?.error || t('superAdmin.apiKeys.saveFailed'));
    } finally {
      setSaving(false);
    }
  }

  function effectiveLine(effective: EffectiveKey) {
    if (effective.source === 'own') {
      return (
        <span className="akp-effective akp-effective-own">
          <span className="akp-effective-dot" />
          {t('superAdmin.apiKeys.ownKey')}
          <span className="akp-hint">••••{effective.key_hint}</span>
        </span>
      );
    }
    if (effective.source === 'inherited') {
      return (
        <span className="akp-effective akp-effective-inherited">
          <span className="akp-effective-dot" />
          {t('superAdmin.apiKeys.inheritedFrom')}{' '}
          {effective.source_tenant_name || effective.source_tenant_id}
          <span className="akp-hint">••••{effective.key_hint}</span>
        </span>
      );
    }
    return (
      <span className="akp-effective akp-effective-platform">
        <span className="akp-effective-dot" />
        {t('superAdmin.apiKeys.usingPlatform')}
      </span>
    );
  }

  function statusChip(key: TenantApiKey) {
    if (key.key_status === 'unhealthy') {
      return (
        <Tooltip title={key.unhealthy_reason || ''}>
          <span className="akp-status akp-status-unhealthy">
            <WarningFilled /> {t('superAdmin.apiKeys.statusUnhealthy')}
          </span>
        </Tooltip>
      );
    }
    if (key.key_status === 'disabled') {
      return (
        <span className="akp-status akp-status-disabled">
          <PauseCircleOutlined /> {t('superAdmin.apiKeys.statusDisabled')}
        </span>
      );
    }
    return (
      <span className="akp-status akp-status-active">
        <CheckCircleFilled /> {t('superAdmin.apiKeys.statusActive')}
      </span>
    );
  }

  if (loading) return <Spin />;

  const visibleProviders = PROVIDERS.filter(p => providers[p.id]);

  return (
    <Card
      size="small"
      title={t('superAdmin.apiKeys.cardTitle')}
      style={{ marginTop: 16 }}
      className="api-keys-panel"
      data-testid="api-keys-panel"
    >
      <p className="akp-desc">{t('superAdmin.apiKeys.description')}</p>

      {visibleProviders.map(({ id: provider, label }) => {
        const data = providers[provider];
        const reordering = reorderingProvider === provider;
        return (
          <section key={provider} className="akp-provider">
            <header className="akp-provider-head">
              <ProviderIcon provider={provider} size={20} />
              <div className="akp-provider-titles">
                <span className="akp-provider-name">{label}</span>
                {effectiveLine(data.effective)}
              </div>
              <Button
                className="akp-add-btn"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => { setAddModalProvider(provider); addForm.resetFields(); }}
              >
                {t('superAdmin.apiKeys.addKey')}
              </Button>
            </header>

            {data.keys.length > 0 && (
              <ul className="akp-key-list">
                {data.keys.map((key, index) => {
                  const isFirst = index === 0;
                  const isLast = index === data.keys.length - 1;
                  const busy = busyKeyId === key.key_id;
                  return (
                    <li
                      key={key.key_id}
                      className={
                        'akp-key-row' +
                        (key.key_status === 'unhealthy' ? ' akp-key-row-unhealthy' : '')
                      }
                    >
                      <span className={`akp-rank ${isFirst ? 'akp-rank-primary' : 'akp-rank-backup'}`}>
                        {isFirst
                          ? t('superAdmin.apiKeys.primaryBadge')
                          : `${t('superAdmin.apiKeys.backupBadge')} ${index}`}
                      </span>

                      <div className="akp-key-ident">
                        <span className="akp-key-name">
                          {key.key_name} <span className="akp-hint">••••{key.key_hint}</span>
                        </span>
                        {key.last_used_at && (
                          <span className="akp-key-meta">
                            {t('superAdmin.apiKeys.colLastUsed')}: {new Date(key.last_used_at).toLocaleString()}
                          </span>
                        )}
                      </div>

                      <Select
                        className="akp-scope-select"
                        size="small"
                        value={key.key_scope}
                        loading={busy}
                        onChange={(v) => patchKey(key.key_id, { key_scope: v })}
                        options={[
                          { value: 'subtree', label: t('superAdmin.apiKeys.scopeSubtree') },
                          { value: 'tenant_only', label: t('superAdmin.apiKeys.scopeTenantOnly') },
                        ]}
                      />

                      {statusChip(key)}

                      <div className="akp-key-actions">
                        <Tooltip title={t('superAdmin.apiKeys.moveUp')}>
                          <Button
                            size="small"
                            type="text"
                            icon={<ArrowUpOutlined />}
                            disabled={isFirst || reordering}
                            onClick={() => moveKey(provider, index, index - 1)}
                          />
                        </Tooltip>
                        <Tooltip title={t('superAdmin.apiKeys.moveDown')}>
                          <Button
                            size="small"
                            type="text"
                            icon={<ArrowDownOutlined />}
                            disabled={isLast || reordering}
                            onClick={() => moveKey(provider, index, index + 1)}
                          />
                        </Tooltip>
                        <span className="akp-actions-divider" />
                        <Tooltip title={t('superAdmin.apiKeys.testKey')}>
                          <Button
                            size="small"
                            type="text"
                            icon={<ExperimentOutlined />}
                            loading={busy}
                            onClick={() => testKey(key.key_id)}
                          />
                        </Tooltip>
                        {key.key_status !== 'active' ? (
                          <Tooltip title={t('superAdmin.apiKeys.reactivate')}>
                            <Button
                              size="small"
                              type="text"
                              icon={<PlayCircleOutlined />}
                              loading={busy}
                              onClick={() => patchKey(key.key_id, { key_status: 'active' })}
                            />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('superAdmin.apiKeys.disable')}>
                            <Button
                              size="small"
                              type="text"
                              icon={<PauseCircleOutlined />}
                              loading={busy}
                              onClick={() => patchKey(key.key_id, { key_status: 'disabled' })}
                            />
                          </Tooltip>
                        )}
                        <Popconfirm
                          title={t('superAdmin.apiKeys.deleteConfirm')}
                          onConfirm={() => deleteKey(key.key_id)}
                        >
                          <Button
                            size="small"
                            type="text"
                            danger
                            icon={<DeleteOutlined />}
                            loading={busy}
                          />
                        </Popconfirm>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        );
      })}

      <Modal
        title={`${t('superAdmin.apiKeys.addKeyTitle')} — ${
          PROVIDERS.find(p => p.id === addModalProvider)?.label || ''
        }`}
        open={addModalProvider !== null}
        onOk={submitAddKey}
        onCancel={() => setAddModalProvider(null)}
        confirmLoading={saving}
        okText={t('common.save')}
        destroyOnClose
      >
        <Form form={addForm} layout="vertical" initialValues={{ key_scope: 'subtree' }}>
          <Form.Item
            name="key_name"
            label={t('superAdmin.apiKeys.keyName')}
            rules={[{ required: true, message: t('superAdmin.apiKeys.keyNameRequired') }]}
          >
            <Input dir="auto" placeholder={t('superAdmin.apiKeys.keyNamePlaceholder')} maxLength={100} />
          </Form.Item>
          <Form.Item
            name="api_key"
            label={t('superAdmin.apiKeys.keyValue')}
            rules={[{ required: true, min: 8, message: t('superAdmin.apiKeys.keyValueRequired') }]}
            extra={t('superAdmin.apiKeys.keyValueHint')}
          >
            <Input.Password dir="auto" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="key_scope" label={t('superAdmin.apiKeys.scope')}>
            <Radio.Group>
              <Radio value="subtree">{t('superAdmin.apiKeys.scopeSubtree')}</Radio>
              <Radio value="tenant_only">{t('superAdmin.apiKeys.scopeTenantOnly')}</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
